import { Directive, inject, OnDestroy, OnInit } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { MatDatepicker, MatDateRangePicker } from '@angular/material/datepicker';
import { getLocaleForLang } from '@constants';
import { DeviceInfoService } from '@core/services/device-info.service';
import { TranslateService } from '@ngx-translate/core';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MobileOverlayBackButtonHandler } from './mobile-overlay-back-button-handler';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'mat-datepicker',
  standalone: false,
})
export class MatDatepickerTouchUiDirective implements OnInit, OnDestroy {
  private readonly datepicker = inject<MatDatepicker<unknown>>(MatDatepicker);
  private readonly dateAdapter = inject<DateAdapter<unknown>>(DateAdapter);
  private readonly deviceInfoService = inject(DeviceInfoService);
  private readonly translateService = inject(TranslateService);
  private readonly destroy$: ReplaySubject<boolean> = new ReplaySubject(1);
  private readonly mobileBackHandler = new MobileOverlayBackButtonHandler({
    isOverlayOpen: () => this.datepicker.opened,
    closeOverlay: () => this.datepicker.close(),
    shouldHandleBackButton: () =>
      this.datepicker.touchUi && this.deviceInfoService.getInfo().deviceTypeDetected === 'PHONE',
  });

  public ngOnInit(): void {
    this.updateTouchUi();
    this.updateDatepickerLocale(this.translateService.currentLang || this.translateService.getFallbackLang());

    this.datepicker.openedStream.pipe(takeUntil(this.destroy$)).subscribe(() => this.mobileBackHandler.handleOpened());
    this.datepicker.closedStream.pipe(takeUntil(this.destroy$)).subscribe(() => this.mobileBackHandler.handleClosed());

    this.deviceInfoService.infoEmitter.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateTouchUi();
    });

    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(({ lang }) => {
      this.updateDatepickerLocale(lang);
    });
  }

  public ngOnDestroy(): void {
    this.mobileBackHandler.destroy();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private updateTouchUi(): void {
    const isPhone = this.deviceInfoService.getInfo().deviceTypeDetected === 'PHONE';
    this.datepicker.touchUi = isPhone;
    this.datepicker.restoreFocus = !isPhone;
  }

  private updateDatepickerLocale(lang: string | null | undefined): void {
    this.dateAdapter.setLocale(getLocaleForLang(lang));
  }
}

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'mat-date-range-picker',
  standalone: false,
})
export class MatDateRangePickerTouchUiDirective implements OnInit, OnDestroy {
  private readonly dateRangePicker = inject<MatDateRangePicker<unknown>>(MatDateRangePicker);
  private readonly dateAdapter = inject<DateAdapter<unknown>>(DateAdapter);
  private readonly deviceInfoService = inject(DeviceInfoService);
  private readonly translateService = inject(TranslateService);
  private readonly destroy$: ReplaySubject<boolean> = new ReplaySubject(1);
  private readonly mobileBackHandler = new MobileOverlayBackButtonHandler({
    isOverlayOpen: () => this.dateRangePicker.opened,
    closeOverlay: () => this.dateRangePicker.close(),
    shouldHandleBackButton: () =>
      this.dateRangePicker.touchUi && this.deviceInfoService.getInfo().deviceTypeDetected === 'PHONE',
  });

  public ngOnInit(): void {
    this.updateTouchUi();
    this.updateDatepickerLocale(this.translateService.currentLang || this.translateService.getFallbackLang());

    this.dateRangePicker.openedStream
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.mobileBackHandler.handleOpened());
    this.dateRangePicker.closedStream
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.mobileBackHandler.handleClosed());

    this.deviceInfoService.infoEmitter.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateTouchUi();
    });

    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(({ lang }) => {
      this.updateDatepickerLocale(lang);
    });
  }

  public ngOnDestroy(): void {
    this.mobileBackHandler.destroy();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private updateTouchUi(): void {
    const isPhone = this.deviceInfoService.getInfo().deviceTypeDetected === 'PHONE';
    this.dateRangePicker.touchUi = isPhone;
    this.dateRangePicker.restoreFocus = !isPhone;
  }

  private updateDatepickerLocale(lang: string | null | undefined): void {
    this.dateAdapter.setLocale(getLocaleForLang(lang));
  }
}
